import { useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { cancelNegotiationWithReason } from '@/services/negotiationCredits';
import { CANCELLATION_CATEGORY_LABELS, type CancellationCategory } from '@/types/negotiationCredits';

export interface CancelNegotiationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  negotiationId: string;
  onCancelled?: () => void;
  onSuccess?: () => void;
}

export default function CancelNegotiationModal({
  open,
  onOpenChange,
  negotiationId,
  onCancelled,
  onSuccess,
}: CancelNegotiationModalProps) {
  const [category, setCategory] = useState<CancellationCategory | ''>('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const handleCancel = async () => {
    if (!category) {
      toast.error('Selecione um motivo de cancelamento');
      return;
    }

    try {
      setLoading(true);
      const result = await cancelNegotiationWithReason(negotiationId, reason, category);
      
      if (!result.success) {
        toast.error(result.error || 'Erro ao cancelar negociação');
        return;
      }

      toast.success('Negociação cancelada');
      onCancelled?.();
      onSuccess?.();
      onOpenChange(false);
    } catch (error: any) {
      toast.error(error.message || 'Erro ao cancelar negociação');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-card border-border">
        <DialogHeader>
          <DialogTitle className="font-display text-foreground flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-destructive" />
            Cancelar Negociação
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Por favor, informe o motivo do cancelamento. Esta informação nos ajuda a melhorar o Marketplace.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Disambiguation notice */}
          <div className="p-3 rounded-xl bg-primary/10 border border-primary/20">
            <p className="text-xs text-foreground">
              <strong className="text-primary">Cancelar é diferente de concluir.</strong>{' '}
              Se a venda já foi realizada com sucesso, feche este aviso e clique em{' '}
              <span className="font-semibold text-success">"Concluir negociação"</span> no chat.
            </p>
          </div>

          {/* Category Select */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Motivo do cancelamento *
            </label>
            <Select value={category} onValueChange={(v) => setCategory(v as CancellationCategory)}>
              <SelectTrigger className="bg-secondary/50 border-border">
                <SelectValue placeholder="Selecione um motivo..." />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(CANCELLATION_CATEGORY_LABELS).map(([key, label]) => (
                  <SelectItem key={key} value={key}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Reason Textarea */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Detalhes (opcional)
            </label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Conte mais sobre o que aconteceu..."
              className="bg-secondary/50 border-border resize-none"
              rows={3}
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground text-right">
              {reason.length}/500
            </p>
          </div>

          {/* Warning */}
          <div className="p-3 rounded-xl bg-warning/10 border border-warning/20">
            <p className="text-xs text-warning">
              ⚠️ O anúncio voltará a ficar disponível no Marketplace. Cancelamentos frequentes podem afetar sua reputação.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
              disabled={loading}
            >
              Voltar
            </Button>
            <Button
              onClick={handleCancel}
              disabled={!category || loading}
              className="flex-1 bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Cancelando...
                </>
              ) : (
                'Sim, cancelar negociação'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
